import React, { useState, useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { Box, Typography, Button } from '@mui/material';
import { Error, Logout, Done, WarningAmber, QrCodeScanner } from '@mui/icons-material';
import { GlobalStyles } from '@mui/material';
import QRCode from 'qrcode';
import API from '../services/api';
import { useAuth } from '../context/AuthContext'
import EventSourceClient from '../services/eventSource';

const DisplayPage = () => {

  const { role, logout } = useAuth();

  const [status, setStatus] = useState('idle'); // idle, qr, success, expired, failed, used, early

  const [idleDisplayMessage, setIdleDisplayMessage] = useState('');
  const [gymNameDisplayMessage, setGymNameDisplayMessage] = useState('');

  const [titleMessage, setTitleMessage] = useState('');
  const [descriptionMessage, setDescriptionMessage] = useState('');

  const [qrCodeDisplayTime, setQrCodeDisplayTime] = useState(30);
  const [scanStatusDisplayTime, setScanStatusDisplayTime] = useState(5);
  const [qrImage, setQrImage] = useState('');
  const [eventSourceClient, setEventSourceClient] = useState(null);
  const [disconnectCount, setDisconnectCount] = useState(0);
  const [scanCount, setScanCount] = useState(0);


  useEffect(() => {
    const client = new EventSourceClient('/display/sse').connect();
    setEventSourceClient(client);

    client.subscribe('settings', (data) => {
      try {
          console.log('New settings message:', data);
          const dataObject = JSON.parse(data);
          setIdleDisplayMessage(dataObject.idleDisplayMessage);
          setGymNameDisplayMessage(dataObject.gymNameDisplayMessage);
          setQrCodeDisplayTime(dataObject.qrCodeDisplayTime);
          setScanStatusDisplayTime(dataObject.scanStatusDisplayTime);
        } catch (error) {
        console.error('Error during handling message:', error);
      }
    });

    client.subscribe('scan', (data) => {
      try {
          console.log('New scan message:', data);
          const dataObject = JSON.parse(data);

          let qrStartDate = '';
          let qrEndDate = '';

          if (dataObject.status === 'success') {
            qrEndDate = format(parseISO(dataObject.qrCode.endDate), 'dd.MM.yyyy');
          } else if (dataObject.status === 'early') {
            qrStartDate = format(parseISO(dataObject.qrCode.startDate), 'dd.MM.yyyy');
          }
          setStatus(dataObject.status); // Set new status
          setScanCount(prev => prev + 1); // Force timer reset even if status unchanged
          updateStatusMessages(dataObject.status, qrStartDate, qrEndDate);
        } catch (error) {
        console.error('Error during handling message:', error);
      }
    });

    client.subscribe('qr', (data) => {
      try {
          console.log('New qr message:', data);
          const dataObject = JSON.parse(data);

          if (dataObject.action === 'display') {
            QRCode.toDataURL(dataObject.code, {
              width: 500,
              height: 500
            })
            .then((dataURL) => {
              setQrImage(dataURL);
              setStatus('qr');
            })
            .catch((error) => {
              console.error('Error generation QR-code:', error);
            });
          } else if (dataObject.action === 'hide') {
              setStatus('idle');
          }
          
        } catch (error) {
        console.error('Error during handling message:', error);
      }
    });

    client.subscribe('error', (data) => {
      setDisconnectCount(prevCount => prevCount + 1);
    });

    client.subscribe('close', (data) => {
      setDisconnectCount(prevCount => prevCount + 1);
    });

    client.subscribe('open', (data) => {
      try {
        console.log('EventSourceClient open event');
        setStatus('idle');
        setDisconnectCount(0);
      } catch (error) {
        console.error('Error during handling message:', error);
      }
    });

    return () => {
      client.unsubscribe('settings');
      client.unsubscribe('scan');
      client.unsubscribe('qr');
      client.unsubscribe('error');
      client.unsubscribe('close');
      client.unsubscribe('open');
      client.close();
    };
  }, []);

  useEffect(() => {
    console.log('Disconnect count changed:', disconnectCount);
    if (disconnectCount >= 3)
      setStatus('connectionError');
    else if (status === 'connectionError')
      setStatus('idle')
  }, [disconnectCount]);

  const timerRef = useRef(null);

  // Handle changing status
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const startTimer = status !== 'idle' && status !== 'connectionError';

    if (startTimer) {
      const newTimer = setTimeout(() => {
        setStatus('idle');
      }, status === 'qr' ? qrCodeDisplayTime * 1000 : scanStatusDisplayTime * 1000);
      timerRef.current = newTimer;
    }
    
    return () => clearTimeout(timerRef.current);
  }, [status, scanCount]);

  const handleLogoutClick = async () => {
     try {
          const res = await API.post('/api/auth/logout');
          logout();
        } catch (err) {
          console.error('Logout error: ', err);
        }
  };

  const updateStatusMessages = (status, startDate, expirationDate) => {
    switch (status) {
      case "success":
        setTitleMessage('Success');
        setDescriptionMessage(`Valid until ${expirationDate}`);
        break;
      case "early":
        setTitleMessage('Early Date');
        setDescriptionMessage(`Valid starting from ${startDate}`);
        break;
      case "expired":
        setTitleMessage('Expired');
        setDescriptionMessage('Please contact the administrator to renew your subscription.');
        break;
      case "used":
        setTitleMessage('Already Used');
        setDescriptionMessage('Please contact the administrator to check your subscription.');
        break;
      case 'failed':
        setTitleMessage('Pass not found');
        setDescriptionMessage('Please make sure you scan correct QR.');
        break;
      default:
        setTitleMessage('');
        setDescriptionMessage('');
    }
  }

  // Rendering depending on status
  return (
    <>
    <GlobalStyles
      styles={{
        html: {
          height: '100%',
          margin: 0,
          padding: 0,
          overflow: 'hidden',
        },
        body: {
          height: '100%',
          margin: 0,
          padding: 0,
          overflow: 'hidden',
        },
      }}
    />
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'background.default',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {role === 'display' && (
        <Button
            variant="outlined"
            color="black"
            sx={{
              position: 'absolute',
              top: '0px',
              right: '0px',
              zIndex: 1,
              opacity: 0.5,
              border: 'none'
            }}
            onClick={() => handleLogoutClick()}
          >
            <Logout />
          </Button>
      )}
      {status === 'idle' && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '16px',
          boxSizing: 'border-box',
        }}>
          <QrCodeScanner variant="h1"
            sx={{
              fontSize: {
                xs: '2rem',
                sm: '3rem',
                md: '4rem',
                lg: '6rem',
                xl: '7rem',
              },
              mb: 2,
            }} />
          <Typography variant="h1" sx={{
              fontSize: {
                  xs: '2rem',
                  sm: '3rem',
                  md: '4rem',
                  lg: '5rem',
                  xl: '6rem',
                },
              mb: 2,
              textAlign: 'center',
              wordWrap: 'break-word',
              width: 'fit-content'
            }}>
            {idleDisplayMessage}
          </Typography>
        </Box>
      )}

      {status === 'qr' && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '16px',
          boxSizing: 'border-box',
        }}>
          <Typography variant="h5" sx={{
                fontSize: {
                  xs: '5rem',
                  sm: '6rem',
                  md: '7rem',
                  lg: '8rem',
                  xl: '9rem',
                },
                textAlign: 'center',
                wordWrap: 'break-word',
                width: 'fit-content'
              }}>
            {gymNameDisplayMessage}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 0,
              width: '100%',
            }}
          >
            <img src={qrImage} alt="QR Code" style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: '500px',
                height: '500px',
                objectFit: 'contain',
              }} />
          </Box>
          <Typography variant="h5" sx={{
                fontSize: {
                  xs: '1.2rem',
                  sm: '1.5rem',
                  md: '2rem',
                  lg: '3rem',
                  xl: '3.5rem',
                },
                textAlign: 'center',
                wordWrap: 'break-word',
                width: 'fit-content'
              }}>
            Please take a photo of this QR code to access the gym.
          </Typography>
        </Box>
      )}

      {status === 'success' && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}>
          <Done variant="h1"
            color="success"
            sx={{
              fontSize: {
                xs: '2rem',
                sm: '3rem',
                md: '4rem',
                lg: '6rem',
                xl: '7rem',
              },
              mb: 2,
            }} />
          <Typography
            variant="h1"
            color="success"
            sx={{
              fontSize: {
                xs: '2rem',
                sm: '3rem',
                md: '4rem',
                lg: '6rem',
                xl: '7rem',
              },
              mb: 2,
            }}
          >
            {titleMessage}
          </Typography>
          <Typography variant="h5" color="success" sx={{
              fontSize: {
                xs: '1rem',
                sm: '1.5rem',
                md: '2rem',
                lg: '3rem',
                xl: '3.5rem',
              },
              textAlign: 'center',
              wordWrap: 'break-word',
              width: 'fit-content'
            }}>
            {descriptionMessage}
          </Typography>
        </Box>
      )}

      {(status === 'expired' ||
        status === 'early' ||
        status === 'used' ||
        status === 'failed'
      ) && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}>
          <WarningAmber variant="h1"
            color="error"
            sx={{
              fontSize: {
                xs: '2rem',
                sm: '3rem',
                md: '4rem',
                lg: '6rem',
                xl: '7rem',
              },
              mb: 2,
            }} />
          <Typography
            variant="h1"
            color="error"
            sx={{
              fontSize: {
                xs: '2rem',
                sm: '3rem',
                md: '4rem',
                lg: '6rem',
                xl: '7rem',
              },
              mb: 2,
            }}
          >
            {titleMessage}
          </Typography>
          <Typography variant="h5" color="error" sx={{
              fontSize: {
                xs: '1rem',
                sm: '1.5rem',
                md: '2rem',
                lg: '3rem',
                xl: '3.5rem',
              },
              textAlign: 'center',
              wordWrap: 'break-word',
              width: 'fit-content'
            }}>
            {descriptionMessage}
          </Typography>
        </Box>
      )}

      {status === 'connectionError' && (
         <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}>
          <Error variant="h1"
            color="error"
            sx={{
              fontSize: {
                xs: '2rem',
                sm: '3rem',
                md: '4rem',
                lg: '6rem',
                xl: '7rem',
              },
              mb: 2,
            }} />
          <Typography
            variant="h1"
            color="error"
            sx={{
              fontSize: {
                xs: '1.5rem',
                sm: '2rem',
                md: '3rem',
                lg: '5rem',
                xl: '6rem',
              },
              mb: 2,
            }}
            >
            Connection Error
          </Typography>
          <Typography variant="h5" color="error" sx={{
                fontSize: {
                  xs: '1rem',
                  sm: '1.5rem',
                  md: '2rem',
                  lg: '3rem',
                  xl: '3.5rem',
                },
                textAlign: 'center',
                wordWrap: 'break-word',
                width: 'fit-content'
              }}>
            Trying to reconnect ({disconnectCount})...
          </Typography>
        </Box>
    )}
      
    </Box>
    </>
  );
};

export default DisplayPage;
